import { useEffect, useState, useCallback, useRef } from 'react';
import { createPaginationContainer, graphql } from 'react-relay/legacy';
import PropTypes from 'prop-types';
import classnames from 'classnames';

// components
import { Button } from 'dibs-elements/exports/Button';
import { Link } from 'dibs-elements/exports/Link';
import { SeoLink } from 'dibs-seo/exports/SeoLink';
import { Spinner } from 'dibs-elements/exports/Spinner';

import { PdpRespSimilarItemsGrid } from '../PdpRespSimilarItems/PdpRespSimilarItemsGrid';

// helpers
import { flattenConnectionNodes } from '../../utils/relayUtils';
import { locations } from '../../utils/tracking/shared/favoritesTracking';
import {
    SIMILAR_ITEMS_LIST,
    VISUALLY_SIMILAR_ITEMS_LIST,
    COLLECTION_MODULE_ITEMS_LIST,
    trackCollectionItemsVisible,
    trackCollectionItemsRendered,
    trackViewAllCollectionItemsClick,
    trackViewMoreVisuallySimilarClick,
    trackViewMoreRelatedItemsClick,
    trackViewLessRelatedItemsClick,
} from '../tracking/similarItemsTracking';

// messages
import {
    youMayAlsoLike,
    visuallySimilar,
    viewMore,
    viewLess,
    viewAllVisuallySimilar,
    viewAllInCollection,
    FeaturedInCollection,
} from '../pdpMessages';

import styles from './PdpRespSimilarItemsFooter.scss';

const MAX_VISUALLY_SIMILAR_GRID_ITEMS = 5;

const removeDupeItems = (ids, visuallySimilarGridItems) => {
    return flattenConnectionNodes(visuallySimilarGridItems)
        .filter(item => !ids.includes(item.serviceId))
        .splice(0, MAX_VISUALLY_SIMILAR_GRID_ITEMS);
};

const PAGE_SIZE = 10;
const ITEMS_PER_ROW = 5;

const handleGridScroll = tileRef => {
    window.scrollTo(
        window.scrollX,
        window.scrollY - (PAGE_SIZE / ITEMS_PER_ROW) * tileRef?.current?.clientHeight
    );
};

export const PdpRespSimilarItemsFooterComponent = ({
    currency,
    viewer,
    user,
    item,
    relayVariables,
    scrollToRef,
    relay,
}) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [hasFetched, setHasFetched] = useState(false);
    const gridItemWrapperRef = useRef();

    const { serviceId: userId } = user || {};
    const { visuallySimilarGridItems } = viewer || {};
    const { serviceId: itemId, isArt, isSold: isSoldPdp, collectionModule } = item;
    const { isClient } = relayVariables;

    const similarItems = flattenConnectionNodes(viewer?.similarItems || []);
    const similarItemsIds = similarItems.map(node => node.serviceId);

    const collectionItems = flattenConnectionNodes(collectionModule?.items || []);

    useEffect(() => {
        // TODO: when we will update relay worth revisiting if client check is still needed
        // At the moment If refetch happens and in the meantime relay vars changes, data gets disposed
        // more info: https://github.com/facebook/relay/blob/1.4.1-manual-patch/packages/react-relay/modern/ReactRelayRefetchContainer.js#L114-L118
        if (isClient && isArt && !isSoldPdp && !visuallySimilarGridItems) {
            relay.refetchConnection(PAGE_SIZE, null, {
                fetchVisuallySimilar: true,
                count: PAGE_SIZE,
            });
        }
    }, [isArt, isSoldPdp, relay, isClient, visuallySimilarGridItems]);

    useEffect(() => {
        if (userId) {
            // reset pagination container when user logs in to prevent broken pagination state
            setIsExpanded(false);
            setHasFetched(false);
        }
    }, [userId]);

    const sharedItemsGridProps = {
        relayVariables: {
            userId: relayVariables.userId,
            isClient: relayVariables.isClient,
        },
        item,
        viewer,
        currency,
        user,
        favoritesLocation: locations.SIMILAR_ITEM_GRID,
        gridItemWrapperClass: styles.similarItems,
        hideBar: true,
    };

    const getButtonState = useCallback(
        function () {
            if (isLoading) {
                return <Spinner size="tiny" containerClass={styles.spinner} />;
            } else if (isExpanded) {
                return viewLess;
            } else {
                return viewMore;
            }
        },
        [isLoading, isExpanded]
    );
    const onViewMoreClick = useCallback(
        function (event) {
            if (isExpanded) {
                trackViewLessRelatedItemsClick(event);
            } else {
                trackViewMoreRelatedItemsClick(event);
            }
            if (relay.hasMore() && !hasFetched) {
                setIsLoading(true);
                relay.loadMore(PAGE_SIZE, () => {
                    setIsLoading(false);
                    setIsExpanded(!isExpanded);
                    setHasFetched(true);
                });
            } else if (hasFetched) {
                setIsExpanded(!isExpanded);
                if (isExpanded) {
                    handleGridScroll(gridItemWrapperRef);
                }
            }
        },
        [isExpanded, relay, hasFetched]
    );

    const similarGridItems = (
        <>
            <PdpRespSimilarItemsGrid
                {...sharedItemsGridProps}
                header={youMayAlsoLike}
                scrollToRef={scrollToRef}
                gridLocation={isSoldPdp ? 'sold pdp you may also like' : SIMILAR_ITEMS_LIST}
                similarItems={isExpanded ? similarItems : similarItems.slice(0, PAGE_SIZE)}
                gridItemWrapperRef={gridItemWrapperRef}
                isDark={!isSoldPdp}
                gridHeaderClass={isSoldPdp ? styles.gridHeaderSoldRedesign : undefined}
            />
            <div
                className={classnames(styles.wrapper, {
                    'primary-viewport-width': !isSoldPdp,
                    [styles.isSoldPdp]: isSoldPdp,
                })}
            >
                <Button
                    className={styles.button}
                    onClick={onViewMoreClick}
                    size="small"
                    type="transparent"
                    dataTn="pdp-similar-items-view-more-button"
                >
                    {getButtonState()}
                </Button>
            </div>
        </>
    );

    const collectionModuleItems = collectionItems.length > 0 && (
        <PdpRespSimilarItemsGrid
            {...sharedItemsGridProps}
            gridHeaderClass={styles.visuallySimilarHeader}
            header={
                <FeaturedInCollection
                    collectionName={collectionModule.name}
                    collectionNameClass={styles.gridHeaderItalics}
                />
            }
            subHeader={
                <div className={`primary-viewport-width ${styles.wrapper}`}>
                    <SeoLink
                        onClick={trackViewAllCollectionItemsClick}
                        linkData={collectionModule.linkData}
                        className={styles.viewMoreLink}
                    >
                        {viewAllInCollection}
                    </SeoLink>
                </div>
            }
            gridLocation={COLLECTION_MODULE_ITEMS_LIST}
            similarItems={collectionItems}
            onComponentDidMount={trackCollectionItemsRendered}
            onContentVisible={trackCollectionItemsVisible}
        />
    );

    const visuallySimilarItems = !collectionModuleItems &&
        !!visuallySimilarGridItems?.edges?.length && (
            <PdpRespSimilarItemsGrid
                {...sharedItemsGridProps}
                gridHeaderClass={styles.visuallySimilarHeader}
                header={visuallySimilar}
                subHeader={
                    <div className={`primary-viewport-width ${styles.wrapper}`}>
                        <Link
                            onClick={event => trackViewMoreVisuallySimilarClick(event)}
                            href={`/similar/?item=${itemId}`}
                            className={styles.viewMoreLink}
                        >
                            {viewAllVisuallySimilar}
                        </Link>
                    </div>
                }
                gridLocation={VISUALLY_SIMILAR_ITEMS_LIST}
                similarItems={removeDupeItems(similarItemsIds, visuallySimilarGridItems)}
            />
        );

    return (
        <div className={styles.container}>
            {similarGridItems}
            {collectionModuleItems}
            {visuallySimilarItems}
        </div>
    );
};

PdpRespSimilarItemsFooterComponent.propTypes = {
    currency: PropTypes.string,
    viewer: PropTypes.object.isRequired,
    user: PropTypes.object,
    item: PropTypes.object.isRequired,
    relay: PropTypes.object,
    scrollToRef: PropTypes.object.isRequired,
    relayVariables: PropTypes.shape({
        userId: PropTypes.string,
        itemId: PropTypes.string.isRequired,
        isClient: PropTypes.bool.isRequired,
        fetchUser: PropTypes.bool.isRequired,
        followSearchPages: PropTypes.array,
        fetchFollowSearch: PropTypes.bool,
    }).isRequired,
};

const connectionConfig = {
    direction: 'forward',
    getConnectionFromProps(props) {
        return props.viewer && props.viewer.similarItems;
    },
    getFragmentVariables(prevVars, totalCount) {
        return {
            ...prevVars,
            count: totalCount,
        };
    },
    getVariables({ relayVariables }, { count, cursor }, fragmentVariables) {
        return {
            count,
            cursor,
            fetchVisuallySimilar: fragmentVariables.fetchVisuallySimilar,
            itemId: fragmentVariables.itemId,
            isTrade: relayVariables.isTrade,
        };
    },
    query: graphql`
        query PdpRespSimilarItemsFooterQuery(
            $fetchVisuallySimilar: Boolean!
            $itemId: String!
            $count: Int
            $cursor: String
            $isTrade: Boolean!
        ) {
            viewer {
                ...PdpRespSimilarItemsFooter_viewer
                    @arguments(
                        fetchVisuallySimilar: $fetchVisuallySimilar
                        count: $count
                        cursor: $cursor
                    )
            }
        }
    `,
};

export const PdpRespSimilarItemsFooter = createPaginationContainer(
    PdpRespSimilarItemsFooterComponent,
    {
        viewer: graphql`
            fragment PdpRespSimilarItemsFooter_viewer on Viewer
            @argumentDefinitions(
                fetchVisuallySimilar: { type: "Boolean", defaultValue: false }
                count: { type: "Int", defaultValue: 10 }
                cursor: { type: "String" }
            ) {
                visuallySimilarGridItems: visualSearch(itemPk: $itemId, first: 6)
                    @include(if: $fetchVisuallySimilar) {
                    edges {
                        node {
                            serviceId
                            ...PdpRespSimilarItemsGrid_similarItems
                        }
                    }
                }
                similarItems: moreLikeThisItem(
                    first: $count
                    after: $cursor
                    seller: EXCLUDE
                    itemId: $itemId
                ) @connection(key: "PdpRespSimilarItemsFooter_similarItems") {
                    edges {
                        node {
                            serviceId
                            ...PdpRespSimilarItemsGrid_similarItems
                        }
                    }
                    pageInfo {
                        hasNextPage
                        startCursor
                        endCursor
                    }
                    totalResults
                }
            }
        `,
        user: graphql`
            fragment PdpRespSimilarItemsFooter_user on User {
                serviceId
                ...PdpRespSimilarItemsGrid_user
            }
        `,
        item: graphql`
            fragment PdpRespSimilarItemsFooter_item on Item {
                isArt
                isSold
                serviceId
                ...PdpRespSimilarItemsGrid_item
                collectionModule {
                    name
                    items(first: 5) {
                        totalResults
                        edges {
                            node {
                                ...PdpRespSimilarItemsGrid_similarItems
                            }
                        }
                    }
                    linkData {
                        ...SeoLink_linkData
                    }
                }
            }
        `,
    },
    connectionConfig
);
